import { RouteRecordRaw } from 'vue-router';

async function requireDevDashboardModules (): Promise<boolean | { name: string }> {
    const { authState } = await import('@lcp/http-auth');
    if (!authState.value.loggedIn) return true;

    const { useUserAccessStore } = await import('@/stores/userAccessStore.ts');
    const userAccessStore = useUserAccessStore();
    try {
        await userAccessStore.ensureShortTermPowerAccessLoaded();
    } catch {
        return { name: 'Home' };
    }

    return userAccessStore.shortTermMarketsConfig?.hasDevDashboardModules === true
        ? true
        : { name: 'Home' };
}

export const marketsRoutes: Record<string, RouteRecordRaw> = {
    redirect: {
        path: '',
        name: 'Power Market Redirect',
        components: {
            default: () => import('@/views/EnactDashboardRedirect.vue'),
        },
        meta: {
            headName: 'Power Market Redirect',
        },
    },
    currentNews: {
        path: 'daily-summary',
        name: 'currentNews',
        components: {
            default: () => import('@/views/powerMarkets/CurrentNewsView.vue'),
        },
        meta: {
            headName: 'Current News',
            accessName: 'current',
            searchMeta: {
                tags: ['daily', 'summary'],
            },
        },
    },
    notifications: {
        path: 'notifications',
        name: 'notifications',
        components: {
            default: () => import('@/views/powerMarkets/NotificationsView.vue'),
        },
        meta: {
            headName: 'Notifications',
            videoLink: 'https://player.vimeo.com/video/508408268',
            accessName: 'notifications',
            searchMeta: {
                tags: ['alerts'],
            },
        },
    },
    monthlySummary: {
        path: 'monthly-summary',
        name: 'monthlySummary',
        components: {
            default: () => import('@/views/powerMarkets/MonthlySummaryView.vue'),
        },
        meta: {
            headName: 'Monthly Summary',
            accessName: 'monthly-summary',
        },
    },
    marketOverview: {
        path: 'market-overview',
        name: 'marketOverview',
        components: {
            default: () => import('@/views/powerMarkets/MarketOverviewDashboards.vue'),
        },
        meta: {
            headName: 'Market Overview',
            accessName: 'dashboard',
        },
    },
    map: {
        path: 'map-page',
        name: 'mapPage',
        components: {
            default: () => import('@/views/powerMarkets/MapPageView.vue'),
        },
        meta: {
            headName: 'GB Map',
            videoLink: 'https://player.vimeo.com/video/508407148',
            accessName: 'map-page',
            searchMeta: {
                tags: ['zonal', 'zones', 'constraints', 'plants'],
            },
        },
    },
    europeMap: {
        path: 'europe-map-page',
        name: 'europeMapPage',
        components: {
            default: () => import('@/views/powerMarkets/EuropeMapView.vue'),
        },
        meta: {
            headName: 'Europe Map',
            videoLink: 'https://player.vimeo.com/video/508407148',
            accessName: 'europe-map-page',
        },
    },
    bingoMap: {
        path: 'bingo-map',
        name: 'mapLive',
        components: {
            default: () => import('@/views/powerMarkets/BingoMapView.vue'),
        },
        meta: {
            headName: 'Bingo Map',
            accessName: 'bingo-map',
            searchMeta: {
                tags: ['zonal', 'zones', 'plant activity'],
            },
        },
    },
    zonalBuildUp: {
        path: 'zonal-build-up',
        name: 'zonalBuildUp',
        components: {
            default: () => import('@/views/powerMarkets/ZonalBuildUpView.vue'),
        },
        meta: {
            headName: 'Zonal Build-Up',
            accessName: 'zonal-build-up',
            searchMeta: {
                tags: ['zonal', 'zones', 'fpn', 'pn', 'indgen'],
            },
        },
    },
    windForecasting: {
        path: 'wind-forecasting',
        name: 'windForecasting',
        components: {
            default: () => import('@/views/powerMarkets/WindForecastingView.vue'),
        },
        meta: {
            headName: 'Wind Forecasting',
            accessName: 'wind-forecasting',
        },
    },
    weatherForecasting: {
        path: 'weather-forecasting',
        name: 'weatherForecasting',
        components: {
            default: () => import('@/views/powerMarkets/WeatherForecastingView.vue'),
        },
        meta: {
            headName: 'Weather Forecasting',
            accessName: 'weather-forecasting',
        },
    },
    news: {
        path: 'news',
        name: 'news',
        components: {
            default: () => import('@/views/powerMarkets/NewsView.vue'),
        },
        meta: {
            headName: 'News',
            accessName: 'news',
            searchMeta: {
                tags: ['market information', 'warnings'],
            },
        },
    },
    plantActivity: {
        path: 'plant-activity-board',
        name: 'plantActivityBoard',
        components: {
            default: () => import('@/views/powerMarkets/PlantActivityBoardView.vue'),
        },
        meta: {
            headName: 'Plant Activity',
            videoLink: 'https://player.vimeo.com/video/508408410',
            pageNeedsLoader: true,
            accessName: 'plantActivityBoard',
            searchMeta: {
                tags: ['asset board'],
            },
        },
    },
    boa: {
        path: 'boa-visualiser/:date?',
        name: 'boa',
        components: {
            default: () => import('@/views/powerMarkets/BoaDashboardView.vue'),
        },
        meta: {
            headName: 'BOA Visualiser',
            videoLink: 'https://player.vimeo.com/video/508406655',
            accessName: 'boa-dashboard',
            searchMeta: {
                tags: ['balancing', 'bm', 'mechanism', 'bids', 'offers', 'niv', 'imbalance'],
            },
        },
    },
    bmDashboards: {
        path: 'bm-dashboards',
        name: 'bmDashboards',
        components: {
            default: () => import('@/views/powerMarkets/BmDashboards.vue'),
        },
        meta: {
            headName: 'BM Dashboards',
            accessName: 'dashboard',
            searchMeta: {
                tags: ['balancing', 'bm', 'mechanism', 'bids', 'offers', 'niv', 'imbalance'],
            },
        },
    },
    europeanBalancingDashboards: {
        path: 'european-balancing-dashboards',
        name: 'europeanBalancingDashboards',
        components: {
            default: () => import('@/views/powerMarkets/EuropeanBalancingDashboards.vue'),
        },
        meta: {
            headName: 'European Balancing',
            accessName: 'dashboard',
            searchMeta: {
                tags: ['imbalance'],
            },
        },
    },
    interconnectorDashboards: {
        path: 'interconnector-dashboards',
        name: 'interconnectorDashboards',
        components: {
            default: () => import('@/views/powerMarkets/europe/InterconnectorDashboards.vue'),
        },
        meta: {
            headName: 'Interconnectors Overview',
            accessName: 'dashboard',
            searchMeta: {
                tags: ['interconnection', 'flows'],
            },
        },
    },
    dayAheadTrading: {
        path: 'day-ahead-trading',
        name: 'dayAheadTrading',
        components: {
            default: () => import('@/views/powerMarkets/DayAheadDashboards.vue'),
        },
        meta: {
            headName: 'Day-Ahead Trading',
            accessName: 'dashboard',
            searchMeta: {
                tags: ['day ahead', 'day-ahead'],
            },
        },
    },
    glossary: {
        path: 'glossary/:definitionId?',
        name: 'glossary',
        components: {
            default: () => import('@/views/powerMarkets/GlossaryView.vue'),
        },
        meta: {
            headName: 'Glossary',
            accessName: 'glossary',
            searchMeta: {
                tags: ['definitions'],
            },
        },
    },
    userDashboard: {
        path: 'dashboard/:dashboardId?',
        name: 'userDashboard',
        props: true,
        components: {
            default: () => import('@/views/powerMarkets/UserDashboardView.vue'),
        },
        meta: {
            headName: 'Custom Dashboards',
            videoLink: 'https://player.vimeo.com/video/508407727',
            showShareButton: true,
            accessName: 'dashboard',
            searchMeta: {
                tags: ['user dashboard'],
            },
        },
    },
    assetMonitoring: {
        path: 'asset-monitoring',
        name: 'assetMonitoring',
        beforeEnter: requireDevDashboardModules,
        components: {
            default: () => import('@/views/powerMarkets/AssetMonitoringView.vue'),
        },
        meta: {
            headName: 'Asset monitoring',
            accessName: 'dashboard',
        },
    },
    singleChart: {
        path: 'single-chart/:seriesId/:optionId/:countryId/:date',
        name: 'singleChart',
        props: true,
        components: {
            default: () => import('@/views/powerMarkets/SingleChartView.vue'),
        },
        meta: {
            headName: 'Single Chart',
        },
    },
    muteDataCheck: {
        path: 'mute-data-check/:seriesId?/:countryId?',
        name: 'muteDataCheck',
        components: {
            default: () => import('@/views/powerMarkets/MuteDataCheckView.vue'),
        },
        meta: {
            headName: 'Mute Data Check',
            searchMeta: {
                isEnactAdmin: true,
            },
        },
    },
    remit: {
        path: 'remit-summary',
        name: 'remit',
        components: {
            default: () => import('@/views/powerMarkets/RemitSummaryView.vue'),
        },
        meta: {
            headName: 'Unavailability',
            videoLink: 'https://player.vimeo.com/video/508408544',
            pageNeedsLoader: true,
            accessName: 'remit-summary',
            searchMeta: {
                tags: ['unavailability', 'outage', 'remit'],
            },
        },
    },
    bingoCard: {
        path: 'bingo-card',
        name: 'bingoCard',
        redirect: { name: 'bingoBoard' },
        meta: {
            headName: 'Bingo Card',
            accessName: 'bingo-card',
            searchMeta: {
                noSearch: true,
            },
        },
    },
    bingoBoard: {
        path: 'bingo-board',
        name: 'bingoBoard',
        components: {
            default: () => import('@/views/powerMarkets/BingoBoardView.vue'),
        },
        meta: {
            headName: 'Bingo Board',
            videoLink: 'https://player.vimeo.com/video/508406370',
            hasPageSettings: true,
            accessName: 'bingo-card',
            searchMeta: {
                tags: ['asset board', 'alert', 'plant activity'],
            },
        },
    },
    historicAnalysis: {
        path: 'historic-exporter',
        name: 'historicAnalysis',
        components: {
            default: () => import('@/views/powerMarkets/HistoricAnalysisView.vue'),
        },
        meta: {
            headName: 'Historic Exporter',
            videoLink: 'https://player.vimeo.com/video/508407243',
            accessName: 'historic-analysis',
            searchMeta: {
                tags: ['data exporter', 'historic analysis'],
            },
        },
    },
    ancillaryContracts: {
        path: 'ancillary-contracts',
        name: 'ancillaryContracts',
        components: {
            default: () => import('@/views/powerMarkets/AncillaryContractsView.vue'),
        },
        meta: {
            headName: 'Ancillary Contracts',
            videoLink: 'https://player.vimeo.com/video/508406072',
            accessName: 'ancillary-contracts',
            searchMeta: {
                tags: ['flexibility',
                    'quick reserve', 'slow reserve', 'QR', 'SR',
                    'Dx', 'D*', 'DM', 'DR', 'DC', 'Dynamic Containment', 'Dynamic Moderation', 'Dynamic Regulation',
                    'FFR', 'STOR', 'MFR', 'DFS', 'Frequency Response', 'Reserve',
                    'CM', 'Capacity Mechanism',
                    'BR', 'Balancing Reserve'],
            },
        },
    },
    ancillaryDashboards: {
        path: 'ancillary-dashboards',
        name: 'ancillaryDashboards',
        components: {
            default: () => import('@/views/powerMarkets/AncillaryDashboards.vue'),
        },
        meta: {
            headName: 'Ancillary Dashboards',
            accessName: 'dashboard',
            searchMeta: {
                tags: ['flexibility',
                    'quick reserve', 'slow reserve', 'QR', 'SR',
                    'Dx', 'D*', 'DM', 'DR', 'DC', 'Dynamic Containment', 'Dynamic Moderation', 'Dynamic Regulation',
                    'FFR', 'STOR', 'MFR', 'DFS', 'Frequency Response', 'Reserve',
                    'CM', 'Capacity Mechanism',
                    'BR', 'Balancing Reserve'],
            },
        },
    },
    forecastHistory: {
        path: 'forecast-evolution',
        name: 'forecastHistory',
        components: {
            default: () => import('@/views/powerMarkets/ForecastHistoryView.vue'),
        },
        meta: {
            headName: 'Forecast Evolution',
            videoLink: 'https://player.vimeo.com/video/508407496',
            accessName: 'forecast-history',
            searchMeta: {
                tags: ['evolution', 'iteration'],
            },
        },
    },
    nivEvolution: {
        path: 'niv-evolution',
        name: 'nivEvolution',
        components: {
            default: () => import('@/views/powerMarkets/NivEvolutionView.vue'),
        },
        meta: {
            headName: 'NIV Evolution',
            accessName: 'dashboard',
            searchMeta: {
                tags: ['evolution', 'iteration'],
            },
        },
    },
    epexGrid: {
        path: 'epex-grid',
        name: 'epexGrid',
        components: {
            default: () => import('@/views/powerMarkets/EpexGridView.vue'),
        },
        meta: {
            headName: 'EPEX Grid',
            blockSmallPortrait: true,
            videoLink: 'https://player.vimeo.com/video/508407007',
            accessName: 'epex-grid',
            searchMeta: {
                tags: ['intraday', 'comtrader', 'm7', 'continuous'],
            },
        },
    },
    epexDashboards: {
        path: 'epex-dashboards',
        name: 'epexOverview',
        components: {
            default: () => import('@/views/powerMarkets/EpexOverviewView.vue'),
        },
        meta: {
            headName: 'EPEX Dashboards',
            accessName: 'dashboard',
            searchMeta: {
                tags: ['intraday', 'comtrader', 'm7', 'continuous'],
            },
        },
    },
    storageDayAheadDashboard: {
        path: 'storage-da-analysis',
        name: 'storageDayAheadDashboard',
        components: {
            default: () => import('@/views/powerMarkets/StorageDayAheadAnalysisView.vue'),
        },
        meta: {
            headName: 'DA Analysis',
            accessName: 'storage-da-analysis',
            searchMeta: {
                tags: ['storage', 'day-ahead', 'day ahead'],
            },
        },
    },
    intradayOverview: {
        path: 'intraday-overview',
        name: 'intradayOverview',
        components: {
            default: () => import('@/views/powerMarkets/IntradayDashboardView.vue'),
        },
        meta: {
            headName: 'Market Overview',
            accessName: 'dashboard',
            searchMeta: {
                tags: ['intraday', 'comtrader', 'm7', 'continuous'],
            },
        },
    },
    intradayTradingOverview: {
        path: 'intraday-trading-overview',
        name: 'intradayTradingOverview',
        components: {
            default: () => import('@/views/powerMarkets/IntradayTradingDashboardView.vue'),
        },
        meta: {
            headName: 'Intraday Trading',
            accessName: 'dashboard',
            searchMeta: {
                tags: ['intraday', 'comtrader', 'm7', 'continuous'],
            },
        },
    },
    europeOverview: {
        path: 'europe-overview',
        name: 'europeOverview',
        components: {
            default: () => import('@/views/powerMarkets/europe/OverviewView.vue'),
        },
        meta: {
            headName: 'Europe Overview',
            accessName: 'europeOverview',
            searchMeta: {
                tags: ['entsoe'],
            },
        },
    },
    europeanAnalysisView: {
        path: 'european-analysis',
        name: 'europeanAnalysisView',
        components: {
            default: () => import('@/views/powerMarkets/europe/EuropeanAnalysisView.vue'),
        },
        meta: {
            headName: 'European Analysis',
            pageNeedsLoader: true,
            accessName: 'europeanAnalysis',
            searchMeta: {
                tags: ['entsoe'],
            },
        },
    },
    valueStreamAnalysis: {
        path: 'value-stream-analysis',
        name: 'valueStreamAnalysis',
        components: {
            default: () => import('@/views/powerMarkets/europe/ValueStreamAnalysisView.vue'),
        },
        meta: {
            headName: 'Value Stream Analysis',
            pageNeedsLoader: true,
            accessName: 'valueStreamAnalysis',
            searchMeta: {
                tags: ['entsoe', 'europe'],
            },
        },
    },
    wholesaleData: {
        path: 'wholesale-data',
        name: 'wholesaleData',
        components: {
            default: () => import('@/views/powerMarkets/europe/WholesaleDataView.vue'),
        },
        meta: {
            headName: 'Wholesale Data',
            pageNeedsLoader: true,
            accessName: 'wholesaleData',
            searchMeta: {
                tags: ['entsoe', 'europe'],
            },
        },
    },
    crossCountryExporter: {
        path: 'cross-country-exporter',
        name: 'universalExporter',
        components: {
            default: () => import('@/views/powerMarkets/europe/UniversalExporterView.vue'),
        },
        meta: {
            headName: 'Cross-Country Exporter',
            pageNeedsLoader: true,
            accessName: 'universalExporter',
            searchMeta: {
                tags: ['entsoe', 'europe'],
            },
        },
    },
    apiInstructions: {
        path: 'api-instructions',
        name: 'apiInstructions',
        components: {
            default: () => import('@/views/powerMarkets/ApiInstructionsView.vue'),
        },
        meta: {
            headName: 'API & DPS instructions',
            accessName: 'apiInstructions',
        },
    },
};
